/**
 * Step 2: Pick the platform → challenge type → (program) → balance.
 *
 * This step owns every "what kind of account" decision so the Firm step
 * can stay about one choice. Platform sits at the top because it's a
 * prereq for everything below it (challenge variants differ per platform
 * for some firms).
 *
 *   Row 0: Platform tiles — renders only when the firm supports >1
 *          playable platform. Single-platform firms pre-fill in step 1.
 *   Row 1: Challenge type tiles — Instant / 1-Step / 2-Step / 3-Step.
 *          ALWAYS active — the catalog is advisory, not a gate. A tile
 *          with zero catalog rows shows its generic sub-label; picking
 *          it drops us into the manual-balance branch (and fires a
 *          narrow synthesis job in the background so the next user's
 *          catalog is warmer).
 *   Row 2: Program tiles — renders only when the picked type has >1
 *          distinct program_name. Single-program types auto-select.
 *   Row 3: Balance grid — renders whenever the picked (firm, type) has
 *          at least one catalog row. Clicking a size finalizes the pick
 *          and sets `firmChallengeRef` to the row id.
 *   Row 3-alt: Manual balance input — renders whenever the picked
 *          (firm, type) has zero catalog rows. `firmChallengeRef` stays
 *          null; the bot verifies the real balance at login.
 */

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { CheckCircle2 } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { ChallengeType, PlatformType } from "@pfh/shared";
import { getFirmChallenges, type FirmChallenge } from "@/lib/api/firms";
import { cn } from "@/lib/utils";
import { PLATFORM_OPTIONS } from "./types";
import type { WizardState } from "./types";

const EASE = [0.16, 1, 0.3, 1] as const;

interface Pick {
  programName: string | null;
  accountSize: number | null;
  steps: number | null;
  challengeType: ChallengeType | null;
  firmChallengeRef: string | null;
}

interface Props {
  state: WizardState;
  onPick: (pick: Pick) => void;
  /** Swap platforms inside the Account step. Clears downstream picks
   *  (type/program/size) because the platform switch might surface a
   *  different catalog of firm_challenges rows. */
  onPlatformChange: (platform: PlatformType) => void;
}

const STANDARD = "Standard" as const;

interface TypeTile {
  steps: number;
  challengeType: ChallengeType;
  label: string;
  sub: string;
}

const TYPE_TILES: TypeTile[] = [
  { steps: 0, challengeType: "instant", label: "Instant Funding", sub: "Live capital from day 1" },
  { steps: 1, challengeType: "1-step", label: "1-Step Evaluation", sub: "One phase, then funded" },
  { steps: 2, challengeType: "2-step", label: "2-Step Challenge", sub: "Challenge + verification" },
  { steps: 3, challengeType: "3-step", label: "3-Step Challenge", sub: "Three phases to funded" },
];

function programKey(name: string | null): string {
  return name && name.trim() !== "" ? name : STANDARD;
}

export function StepProgram({ state, onPick, onPlatformChange }: Props) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [challenges, setChallenges] = useState<FirmChallenge[]>([]);
  const [manualSize, setManualSize] = useState(
    state.accountSize != null ? String(state.accountSize) : "",
  );

  const firmSlug = state.firm?.id ?? null;

  // Platforms the picked firm actually supports AND we have a driver
  // for. Empty when the firm has nothing playable — rare but we want
  // to render an explicit "no driver" message rather than a blank UI.
  const playablePlatforms = useMemo(() => {
    const raw = state.firm?.supported_platforms ?? [];
    const ready = new Set(
      PLATFORM_OPTIONS.filter((o) => o.driverReady).map((o) => o.type),
    );
    return raw.filter((p): p is PlatformType => ready.has(p as PlatformType));
  }, [state.firm]);
  const showPlatformRow = playablePlatforms.length > 1;
  const isFuturesPlatform =
    state.platform === "tradovate" || state.platform === "ninjatrader" || state.platform === "blackarrow";
  const typeTiles = isFuturesPlatform
    ? TYPE_TILES.filter((tile) => tile.challengeType === "1-step")
    : TYPE_TILES;

  useEffect(() => {
    if (!isFuturesPlatform) return;
    if (state.steps === 1 && state.challengeType === "1-step") return;
    onPick({
      programName: null,
      accountSize: null,
      steps: 1,
      challengeType: "1-step",
      firmChallengeRef: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFuturesPlatform, state.steps, state.challengeType]);

  useEffect(() => {
    if (!firmSlug) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getFirmChallenges(firmSlug)
      .then((res) => {
        if (cancelled) return;
        setChallenges(res.challenges);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmSlug]);

  // Per-type aggregates for the tile row.
  const typeAggregates = useMemo(() => {
    const map = new Map<number, { count: number; minPrice: number }>();
    for (const c of challenges) {
      const cur = map.get(c.steps) ?? { count: 0, minPrice: Number.POSITIVE_INFINITY };
      cur.count += 1;
      if (c.effective_price > 0 && c.effective_price < cur.minPrice) {
        cur.minPrice = c.effective_price;
      }
      map.set(c.steps, cur);
    }
    return map;
  }, [challenges]);

  const hasAnyChallenges = challenges.length > 0;
  const unseeded = !loading && !hasAnyChallenges;

  // Programs available for the picked challenge type.
  const programsForType = useMemo(() => {
    if (state.steps == null) return [];
    const byKey = new Map<
      string,
      { name: string | null; sizes: Set<number>; minPrice: number }
    >();
    for (const c of challenges) {
      if (c.steps !== state.steps) continue;
      const k = programKey(c.program_name);
      const cur = byKey.get(k) ?? {
        name: c.program_name,
        sizes: new Set<number>(),
        minPrice: Number.POSITIVE_INFINITY,
      };
      cur.sizes.add(c.account_size);
      if (c.effective_price > 0 && c.effective_price < cur.minPrice) {
        cur.minPrice = c.effective_price;
      }
      byKey.set(k, cur);
    }
    return Array.from(byKey.entries()).map(([key, v]) => ({
      key,
      name: v.name,
      sizeCount: v.sizes.size,
      fromPrice: Number.isFinite(v.minPrice) ? v.minPrice : null,
    }));
  }, [challenges, state.steps]);

  // Size rows for the (type, program) pair. When the firm has a single
  // program for this type we ignore program_name entirely.
  const variantsForPick = useMemo(() => {
    if (state.steps == null) return [] as FirmChallenge[];
    const singleProgram = programsForType.length <= 1;
    return challenges
      .filter((c) => {
        if (c.steps !== state.steps) return false;
        if (singleProgram) return true;
        return programKey(c.program_name) === programKey(state.programName);
      })
      .sort((a, b) => a.account_size - b.account_size);
  }, [challenges, programsForType.length, state.programName, state.steps]);

  const showProgramRow = state.steps != null && programsForType.length > 1;
  const programReady =
    state.steps != null && (!showProgramRow || state.programName !== null);

  // Auto-select the single program so the Size grid shows up without a
  // pointless extra click.
  useEffect(() => {
    if (loading) return;
    if (state.steps == null) return;
    if (state.programName !== null) return;
    if (programsForType.length === 1 && programsForType[0]) {
      onPick({
        programName: programsForType[0].name,
        accountSize: state.accountSize,
        steps: state.steps,
        challengeType: state.challengeType,
        firmChallengeRef: state.firmChallengeRef,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, programsForType.length, state.steps]);

  const pickType = (tile: TypeTile) => {
    onPick({
      programName: null,
      accountSize: null,
      steps: tile.steps,
      challengeType: tile.challengeType,
      firmChallengeRef: null,
    });
    // Catalog doesn't cover this bucket yet — fire-and-forget a narrow
    // synthesis job so the next user adding the same firm+type gets
    // pre-filled rules. No await: the wizard never waits on the catalog.
    const agg = typeAggregates.get(tile.steps);
    if (firmSlug && (agg == null || agg.count === 0)) {
      void fetch(
        `/api/firms/${encodeURIComponent(firmSlug)}/synthesize-challenges?type=${encodeURIComponent(tile.challengeType)}`,
        { method: "POST", credentials: "include" },
      ).catch(() => {
        /* fire-and-forget — the wizard doesn't depend on this */
      });
    }
  };

  const pickProgram = (name: string | null) => {
    onPick({
      programName: name,
      accountSize: null,
      steps: state.steps,
      challengeType: state.challengeType,
      firmChallengeRef: null,
    });
  };

  const pickVariant = (c: FirmChallenge) => {
    onPick({
      programName: c.program_name,
      accountSize: c.account_size,
      steps: c.steps,
      challengeType: state.challengeType,
      firmChallengeRef: String(c.id),
    });
  };

  const applyManualSize = () => {
    const num = parseInt(manualSize, 10);
    if (!Number.isFinite(num) || num <= 0) return;
    onPick({
      programName: null,
      accountSize: num,
      steps: state.steps,
      challengeType: state.challengeType,
      firmChallengeRef: null,
    });
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        Loading {state.firm?.name ?? "firm"} programs…
      </div>
    );
  }

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Couldn&rsquo;t load programs: {loadError}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Row 0 · Platform ──────────────────────────────── */}
      {showPlatformRow && (
        <section className="space-y-2">
          <SectionHeader
            title="Platform"
            hint={`${state.firm?.name ?? "This firm"} runs on more than one platform — pick the one your account is on.`}
          />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {playablePlatforms.map((pt) => {
              const opt = PLATFORM_OPTIONS.find((o) => o.type === pt);
              const active = state.platform === pt;
              return (
                <button
                  key={pt}
                  type="button"
                  onClick={() => onPlatformChange(pt)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl border border-border/40 bg-card/80 p-3 text-left transition-all hover:border-accent/40 hover:bg-accent/5",
                    active && "border-accent/60 bg-accent/10 ring-1 ring-accent/40",
                  )}
                >
                  {opt?.logo && (
                    <img
                      src={opt.logo}
                      alt=""
                      className="h-6 w-6 shrink-0"
                      aria-hidden
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        "truncate text-sm font-semibold",
                        active ? "text-accent" : "text-foreground",
                      )}
                    >
                      {opt?.label ?? pt}
                    </div>
                  </div>
                  {active && <CheckCircle2 className="h-4 w-4 shrink-0 text-accent" />}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Row 1 · Challenge type ────────────────────────── */}
      <section className="space-y-2">
        <SectionHeader
          title="Account Type"
          hint={
            isFuturesPlatform
              ? "Futures prop firms use one-step evaluations here. Consistency is enforced during evaluation, not funded."
              : unseeded
                ? "We haven’t catalogued this firm yet — pick the type and type your balance below."
                : typeAggregates.size < TYPE_TILES.length
                  ? "Pick the track you bought. If we haven’t catalogued that variant, type your balance — the bot confirms at login."
                  : "Pick the evaluation track you bought."
          }
        />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {typeTiles.map((tile) => {
            const agg = typeAggregates.get(tile.steps);
            const catalogued = agg != null && agg.count > 0;
            const active = state.steps === tile.steps;
            return (
              <button
                key={tile.steps}
                type="button"
                onClick={() => pickType(tile)}
                className={cn(
                  "flex flex-col gap-0.5 rounded-xl border border-border/40 bg-card/80 p-3 text-left transition-all hover:border-accent/40 hover:bg-accent/5",
                  active && "border-accent/60 bg-accent/10 ring-1 ring-accent/40",
                )}
              >
                <div
                  className={cn(
                    "text-sm font-semibold",
                    active ? "text-accent" : "text-foreground",
                  )}
                >
                  {tile.label}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {catalogued
                    ? `${agg!.count} variant${agg!.count === 1 ? "" : "s"}${
                        Number.isFinite(agg!.minPrice)
                          ? ` · from $${agg!.minPrice}`
                          : ""
                      }`
                    : tile.sub}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Row 2 · Program (conditional) ─────────────────── */}
      <AnimatePresence>
        {showProgramRow && (
          <motion.section
            key="programs"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="space-y-2"
          >
            <SectionHeader
              title="Program"
              hint="This firm sells more than one variant — pick the exact track you bought."
            />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {programsForType.map((p, i) => {
                const selected = programKey(state.programName) === p.key;
                return (
                  <motion.div
                    key={p.key}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03, duration: 0.2, ease: EASE }}
                  >
                    <Card
                      className={cn(
                        "cursor-pointer transition-all hover:border-accent/40 hover:bg-accent/5",
                        selected && "border-accent/60 bg-accent/5 ring-2 ring-accent/40",
                      )}
                      onClick={() => pickProgram(p.name)}
                    >
                      <CardContent className="flex items-center gap-3 p-3">
                        <div
                          className={cn(
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/50 text-xs font-semibold",
                            selected && "bg-accent/10 text-accent",
                          )}
                        >
                          {p.key.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{p.key}</div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {p.sizeCount} size{p.sizeCount === 1 ? "" : "s"}
                            {p.fromPrice != null && ` · from $${p.fromPrice}`}
                          </div>
                        </div>
                        {selected && (
                          <CheckCircle2 className="h-4 w-4 text-accent" />
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Row 3 · Balance ───────────────────────────────── */}
      <AnimatePresence>
        {programReady && variantsForPick.length > 0 && (
          <motion.section
            key="balance"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="space-y-2"
          >
            <SectionHeader
              title="Balance"
              hint="Pick the account size the firm invoiced, then review the detected rules before continuing."
            />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {variantsForPick.map((v) => {
                const selected = state.accountSize === v.account_size;
                return (
                  <button
                    key={`${v.id}`}
                    type="button"
                    onClick={() => pickVariant(v)}
                    className={cn(
                      "flex flex-col items-start gap-0.5 rounded-lg border border-border/40 bg-card/80 px-3 py-2.5 text-left transition-all hover:border-accent/40 hover:bg-accent/5",
                      selected && "border-accent/60 bg-accent/10 ring-1 ring-accent/40",
                    )}
                  >
                    <div
                      className={cn(
                        "font-mono text-sm font-semibold",
                        selected ? "text-accent" : "text-foreground",
                      )}
                    >
                      ${v.account_size.toLocaleString()}
                    </div>
                    {v.effective_price > 0 && (
                      <div className="text-[10px] text-muted-foreground">
                        ${v.effective_price}
                      </div>
                    )}
                    <div className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                      {v.profit_target ?? (v.profit_target_pct != null ? `${v.profit_target_pct}% Target` : "Target Unknown")}
                    </div>
                  </button>
                );
              })}
            </div>
            {state.firmChallengeRef && (
              <VariantRulePreview
                variant={variantsForPick.find((v) => String(v.id) === state.firmChallengeRef) ?? null}
              />
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Manual size fallback ───────────────────────────
          Fires whenever the picked (firm, type) has no catalog rows —
          whether the firm is fully unseeded or just this one type is
          uncovered. The bot verifies the real balance at login, so the
          catalog is advisory, not a gate. */}
      <AnimatePresence>
        {state.steps != null && variantsForPick.length === 0 && (
          <motion.section
            key="manual-size"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="space-y-2"
          >
            <SectionHeader
              title="Balance (USD)"
              hint={
                unseeded
                  ? "Your firm’s exact account size — we’ll use it as the reference balance."
                  : "We haven’t catalogued this variant yet — type your exact balance and the bot will confirm it at login."
              }
            />
            <div className="flex gap-2">
              <Input
                inputMode="numeric"
                value={manualSize}
                onChange={(e) => setManualSize(e.target.value.replace(/[^0-9]/g, ""))}
                onBlur={applyManualSize}
                placeholder="e.g. 10000"
                className="h-10 font-mono"
              />
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}

function VariantRulePreview({ variant }: { variant: FirmChallenge | null }) {
  if (!variant) return null;
  const rows = [
    ["Targets", variant.profit_target ?? pctLabel(variant.profit_target_pct)],
    ["Daily Loss", variant.daily_loss ?? pctLabel(variant.daily_loss_pct)],
    ["Max Loss", variant.max_loss ?? pctLabel(variant.max_loss_pct)],
    ["Leverage", variant.leverage],
  ].filter(([, value]) => value);
  if (rows.length === 0) return null;
  return (
    <div className="grid gap-2 rounded-xl border border-border/40 bg-muted/20 p-3 sm:grid-cols-4">
      {rows.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {label}
          </p>
          <p className="mt-1 truncate text-xs font-medium text-foreground">{value}</p>
        </div>
      ))}
    </div>
  );
}

function pctLabel(value: number | null | undefined): string | null {
  return value == null ? null : `${value}%`;
}

function SectionHeader({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="space-y-0.5">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div className="text-[11px] text-muted-foreground/80">{hint}</div>
    </div>
  );
}
